import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants, selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { uploadAttachments } from 'pages/requestForm/actions';
import { getIsExchangeRateInputAvailable } from 'pages/requestForm/selectors/requestSelectors';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUserBetaFeatures } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';

import { AttachmentsSection, MemoSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { getContext } from '../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../selectors/qbooks';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import JournalEntryFooter from './components/(legacy)/JournalEntryFooter/JournalEntryFooter';
import JournalEntryHeaderFields from './components/JournalEntryHeaderFields/JournalEntryHeaderFields';
import JournalEntryLines from './components/JournalEntryLines/JournalEntryLines';
import { JournalEntrySummary } from './components/JournalEntrySummary/JournalEntrySummary';
import { useFields, useJournalEntryLines, usePayees, useTaxCodeList } from './QBooksJournalEntryRequestForm.hooks';
import { messages } from './QBooksJournalEntryRequestForm.messages';
import {
    Content,
    ContentInner,
    Root,
    Toolbar,
    ToolbarHeader,
    ToolbarHeaderText,
} from './QBooksJournalEntryRequestForm.styles';
import { QBooksJournalEntryRequestFormProps } from './QBooksJournalEntryRequestForm.types';
import {
    calculateAmounts,
    checkJournalEntryLineIsEmpty,
    checkJournalEntryLinesAreEmpty,
    getValidationErrors,
} from './QBooksJournalEntryRequestForm.utils';

const { qBooksConstants } = constants;

const QBooksJournalEntryRequestForm = memo<QBooksJournalEntryRequestFormProps>((props) => {
    const { request } = props;

    const dispatch = useDispatch();

    const pageContext = useSelector(getContext);
    const qbooksContext = useSelector(getQBooksContext);
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    const company = useSelector((state) => selectors.company.findCompanyById(state, request.companyId));

    const {
        fieldValues,
        onFieldChange,
        onCurrencyChange,
        setFieldsValuesBulk,
        onExchangeRateChange,
        onAutoExchangeRateChange,
    } = useFields({
        companyCurrency: request.companyCurrency,
    });

    const isExchangeRateInputAvailable = getIsExchangeRateInputAvailable(request);

    const [showErrors, setShowErrors] = useState(false);

    const shouldPrefillRequestDataRef = useRef(true);

    const currentDate = new Date().toISOString();

    const taxCodeList = useTaxCodeList(fieldValues.journalDate || currentDate);

    const {
        journalEntryLines,
        setJournalEntryLines,
        onJournalEntryLineAdd,
        onJournalEntryLineClone,
        onJournalEntryLineFieldUpdate,
        onJournalEntryLineRemove,
        onJournalEntryLinesReorder,
    } = useJournalEntryLines(fieldValues.lineAmountType, taxCodeList);

    const { data: payeesData } = usePayees(request.id, request.companyId);

    useEffect(() => {
        if (taxCodeList.length) {
            journalEntryLines.forEach((entryLine) => {
                if (
                    entryLine.taxCode !== null &&
                    !taxCodeList.some((taxCode) => entryLine.taxCode?.id === taxCode.id)
                ) {
                    onJournalEntryLineFieldUpdate(entryLine.id, 'taxCode', null);
                }
            });
        }
    }, [fieldValues.journalDate, taxCodeList, journalEntryLines, onJournalEntryLineFieldUpdate]);

    useEffect(() => {
        if (!shouldPrefillRequestDataRef.current) {
            return;
        }

        const details = request.details;

        const isDraft = request.statusV2 === domain.RequestStatusV2.Draft;

        let currency = qbooksContext.currencies?.find((currency) => currency.id === request.currency);

        if (isDraft && !currency && Array.isArray(qbooksContext.currencies) && qbooksContext.currencies.length > 0) {
            currency = qbooksContext.currencies[0];
        }

        setFieldsValuesBulk({
            currency: currency ?? {
                id: request.currency,
                text: request.currency,
            },
            journalDate: details.date,
            journalNumber: details.number,
            memo: details.memo,
            lineAmountType: details.lineAmountType || domain.LineAmountType.TaxExclusive,
            exchangeRate: request.isManualExchangeRate ? request.currencyExchangeRate : null,
            isManualExchangeRate: request.isManualExchangeRate,
        });

        setJournalEntryLines(
            (details.lines || []).map((line) => ({
                ...line,
                taxCode:
                    taxCodeList.find(
                        (taxCodeItem) =>
                            taxCodeItem.taxCodeId === line.taxCode?.id &&
                            taxCodeItem.taxApplicableOnType === line.taxApplicableOnType
                    ) || null,
            }))
        );

        shouldPrefillRequestDataRef.current = false;
    }, [
        request.details,
        request.currency,
        request.currencyExchangeRate,
        request.exchangeRate,
        request.isManualExchangeRate,
        request.statusV2,
        qbooksContext.currencies,
        setFieldsValuesBulk,
        setJournalEntryLines,
        taxCodeList,
    ]);

    const onUploadAttachments = useCallback(
        (files: File[]) => {
            dispatch(uploadAttachments({ files }));
        },
        [dispatch]
    );

    const onLineAmountTypeChange = useCallback(
        (value: { id: domain.LineAmountType }) => {
            onFieldChange('lineAmountType', value.id);
        },
        [onFieldChange]
    );

    const showErrorsOnEmptyLine = useMemo(() => {
        return checkJournalEntryLinesAreEmpty(journalEntryLines);
    }, [journalEntryLines]);

    const getRequestTransfer = (): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const amounts = calculateAmounts(
            journalEntryLines,
            fieldValues.lineAmountType,
            fieldValues.journalDate || currentDate,
            qbooksContext
        );

        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            amount: amounts.debitTotal,
            currency: fieldValues.currency?.id ?? null,
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: request.requestNote.value,
            templateVersion: pageContext.templateVersion,
            companyId: request.companyId,
        };

        if (fieldValues.isManualExchangeRate) {
            transfer.exchangeRate = fieldValues.exchangeRate;
        } else {
            transfer.exchangeRate = fieldValues.autoExchangeRate;
            transfer.exchangeRateSource = fieldValues.exchangeRateSource;
            transfer.exchangeRateDate = fieldValues.exchangeRateDate;
        }

        transfer.qBooksJournalEntryDetails = {
            date: fieldValues.journalDate,
            number: fieldValues.journalNumber,
            memo: fieldValues.memo,
            lineAmountType: fieldValues.lineAmountType,
            debitSubTotal: amounts.debitSubTotal,
            creditSubTotal: amounts.creditSubTotal,
            debitTotalTax: amounts.debitTotalTax,
            creditTotalTax: amounts.creditTotalTax,
            lines: journalEntryLines
                .filter((line) => !checkJournalEntryLineIsEmpty(line))
                .map((line) => ({
                    id: line.id.startsWith('qbooksJournalEntryLine') ? null : line.id,
                    accountId: line.account?.id || null,
                    amount: line.amount,
                    amountSide: line.amountType,
                    description: line.description,

                    taxCode: line.taxCode?.taxCodeId || null,
                    taxApplicableOnType: line.taxCode?.taxApplicableOnType || domain.TaxApplicableOnType.None,
                    payee:
                        line.payee && line.payeeType
                            ? {
                                  id: line.payee.id,
                                  type: line.payeeType.id,
                              }
                            : null,
                    classId: line.class?.id || null,
                    departmentId: line.department?.id || null,
                })),
            taxComponents: Object.values(amounts.taxes).map((taxLine) => ({
                taxPercent: taxLine.taxPercent,
                taxCodeId: taxLine.taxCodeId,
                taxRateId: taxLine.taxRateId,
                taxApplicableOnType: taxLine.taxApplicableOnType,
                taxCreditAmount: taxLine.credit,
                taxDebitAmount: taxLine.debit,
                taxCodeName: taxLine.taxName,
                taxableDebitAmount: taxLine.taxableDebitAmount,
                taxableCreditAmount: taxLine.taxableCreditAmount,
            })),
        };

        return transfer;
    };

    const isClassFieldRequired = qbooksContext.classField
        ? pageContext.requiredFieldIds.includes(qbooksContext.classField.id)
        : false;
    const isDepartmentFieldRequired = qbooksContext.departmentField
        ? pageContext.requiredFieldIds.includes(qbooksContext.departmentField.id)
        : false;

    const getRequestValidationErrors = useCallback(async () => {
        setShowErrors(true);

        return getValidationErrors(
            fieldValues,
            journalEntryLines,
            isClassFieldRequired,
            isDepartmentFieldRequired,
            qbooksContext.accounts
        );
    }, [fieldValues, journalEntryLines, isClassFieldRequired, isDepartmentFieldRequired, qbooksContext.accounts]);

    const onChangeMemo = useCallback(
        (newValue: string | null) => {
            onFieldChange('memo', newValue);
        },
        [onFieldChange]
    );

    const isAUOrg = company?.integration?.countryCode === backend.OrganisationVersion.QBO_AU;
    const isUSOrg = company?.integration?.countryCode === backend.OrganisationVersion.QBO_US;

    const { isBelgradeUIFilesUploadSection, isBelgradeUILineItemsTotalSection } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider
                    maxSize={pageContext.attachmentMaxSize}
                    allowedFileExtensions={qBooksConstants.QBO_ALLOWED_FILE_EXTENSIONS}
                    onUpload={onUploadAttachments}
                >
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarHeader>
                        <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                        <ActionsBlock
                            customGetRequestTransfer={getRequestTransfer}
                            customRequestValidationFn={getRequestValidationErrors}
                        />
                    </ToolbarHeader>
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <JournalEntryHeaderFields
                            fieldValues={fieldValues}
                            currencies={qbooksContext.currencies || []}
                            showCurrencyField={qbooksContext.isMultiCurrencyEnabled}
                            showErrors={showErrors}
                            onFieldChange={onFieldChange}
                            onCurrencyChange={onCurrencyChange}
                        />

                        <JournalEntryLines
                            requestId={request.id}
                            companyId={request.companyId}
                            companyCurrency={request.companyCurrency}
                            requestCurrency={fieldValues.currency?.id ?? request.companyCurrency}
                            journalDate={fieldValues.journalDate}
                            lines={journalEntryLines}
                            payees={payeesData}
                            taxCodeList={taxCodeList}
                            lineAmountType={fieldValues.lineAmountType}
                            isAUOrg={isAUOrg}
                            isUSOrg={isUSOrg}
                            isClassFieldRequired={isClassFieldRequired}
                            isDepartmentFieldRequired={isDepartmentFieldRequired}
                            showErrors={showErrors}
                            showErrorsOnEmptyLine={showErrorsOnEmptyLine}
                            exchangeRate={fieldValues.exchangeRate}
                            isManualExchangeRate={fieldValues.isManualExchangeRate}
                            onAddLine={onJournalEntryLineAdd}
                            onCloneLine={onJournalEntryLineClone}
                            onRemoveLine={onJournalEntryLineRemove}
                            onReorderLines={onJournalEntryLinesReorder}
                            onLineFieldUpdate={onJournalEntryLineFieldUpdate}
                            onLineAmountTypeChange={onLineAmountTypeChange}
                            onExchangeRateChange={onExchangeRateChange}
                            onAutoExchangeRateChange={onAutoExchangeRateChange}
                            isExchangeRateInputAvailable={!!isExchangeRateInputAvailable}
                        />

                        {isBelgradeUILineItemsTotalSection ? (
                            <JournalEntrySummary
                                lines={journalEntryLines}
                                currency={fieldValues.currency}
                                lineAmountType={fieldValues.lineAmountType}
                                date={fieldValues.journalDate || currentDate}
                            />
                        ) : (
                            <JournalEntryFooter
                                lines={journalEntryLines}
                                currency={fieldValues.currency}
                                lineAmountType={fieldValues.lineAmountType}
                                date={fieldValues.journalDate || currentDate}
                            />
                        )}

                        <ConditionWrapper
                            condition={isBelgradeUIFilesUploadSection}
                            wrapperIf={(children) => (
                                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                                    {children}

                                    <AttachmentsSection
                                        request={request}
                                        extensions={qBooksConstants.qboAllowedFileExtensions}
                                    />
                                </Grid>
                            )}
                        >
                            <MemoSection
                                defaultValue={request.details?.memo || ''}
                                disabled={ocrIsLoading}
                                maxLength={constants.qBooksConstants.MEMO_MAX_LENGTH}
                                onChange={onChangeMemo}
                            />
                        </ConditionWrapper>

                        {!isBelgradeUIFilesUploadSection && <FilesSection />}
                    </ContentInner>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

export default QBooksJournalEntryRequestForm;
